// ============================================================
//  src/pages/auth/LoginPage.js — Matches PHP login.php UI
// ============================================================
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { getError } from '../../utils/helpers';
import './auth.css';

export default function LoginPage() {
  const { login } = useAuth();
  const navigate  = useNavigate();
  const [form, setForm]       = useState({ identifier: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [showPass, setShowPass] = useState(false);

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.identifier.trim() || !form.password) {
      setError('Please fill in all fields.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await login(form.identifier.trim(), form.password);
      if (res.success) {
        toast.success('Welcome back! 👋');
        navigate('/dashboard');
      } else if (res.code === 'unverified') {
        toast.error('Please verify your email first.');
        navigate('/verify-email', { state: { email: res.email } });
      } else {
        setError(res.message || 'Login failed. Please try again.');
      }
    } catch (err) {
      const msg = getError(err);
      if (err?.response?.data?.code === 'unverified') {
        toast.error('Please verify your email first.');
        navigate('/verify-email', { state: { email: err.response.data.email } });
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      {/* ── Left panel ── */}
      <div className="auth-left">
        <div className="auth-dot-grid" />
        <div className="auth-ring auth-r1" />
        <div className="auth-ring auth-r2" />
        <div className="auth-ring auth-r3" />

        <div className="auth-left-content">
          <span className="auth-brand">
            <div className="auth-brand-mark">C</div>
            <span className="auth-brand-name">Cloverr</span>
          </span>
        </div>

        <div className="auth-left-main">
          <div className="auth-eyebrow">
            <div className="auth-eyebrow-dot" />
            <span>Platform Live</span>
          </div>
          <div className="auth-headline">
            Earn Money<br />Completing <em>Tasks</em>
          </div>
          <div className="auth-sub">
            Complete social media tasks and get paid instantly. Join thousands earning daily.
          </div>
          <div className="auth-features">
            <div className="auth-feat">
              <div className="auth-feat-icon"><i className="fas fa-wallet" /></div>
              <div className="auth-feat-text">
                <strong>Instant Wallet</strong>
                <span>Fund and manage your balance</span>
              </div>
            </div>
            <div className="auth-feat">
              <div className="auth-feat-icon"><i className="fas fa-tasks" /></div>
              <div className="auth-feat-text">
                <strong>Real Tasks</strong>
                <span>Social media tasks that pay</span>
              </div>
            </div>
            <div className="auth-feat">
              <div className="auth-feat-icon"><i className="fas fa-shield-halved" /></div>
              <div className="auth-feat-text">
                <strong>Secure Platform</strong>
                <span>Your earnings are protected</span>
              </div>
            </div>
          </div>
        </div>

        <div className="auth-left-footer">
          <div className="auth-ssl">
            <i className="fas fa-lock" /> 256-bit SSL encrypted
          </div>
        </div>
      </div>

      {/* ── Right panel ── */}
      <div className="auth-right">
        <div className="auth-shell">
          <div className="auth-hdr">
            <h2>Welcome back 👋</h2>
            <p>Sign in to your Cloverr account.</p>
          </div>

          {error && (
            <div className="auth-alert auth-alert-error">
              <i className="fas fa-circle-exclamation" /> {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="auth-field">
              <label>Email or Phone Number</label>
              <div className="auth-field-wrap">
                <i className="fas fa-user fi" />
                <input
                  type="text"
                  placeholder="Enter email or phone"
                  value={form.identifier}
                  onChange={set('identifier')}
                  autoComplete="username"
                />
              </div>
            </div>

            <div className="auth-field">
              <label>Password</label>
              <div className="auth-field-wrap">
                <i className="fas fa-lock fi" />
                <input
                  type={showPass ? 'text' : 'password'}
                  placeholder="Your password"
                  value={form.password}
                  onChange={set('password')}
                  autoComplete="current-password"
                />
                <i
                  className={`fas ${showPass ? 'fa-eye-slash' : 'fa-eye'} fi fi-right`}
                  onClick={() => setShowPass(!showPass)}
                />
              </div>
            </div>

            <div className="auth-forgot">
              <Link to="/forgot-password">Forgot password?</Link>
            </div>

            <button type="submit" className="btn-auth" disabled={loading}>
              {loading
                ? <><span className="spinner" /> Signing in…</>
                : <><i className="fas fa-right-to-bracket" /> Sign In</>
              }
            </button>
          </form>

          <div className="auth-bottom">
            Don't have an account? <Link to="/register">Create one</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
